import { useEffect, useRef, useState } from 'react';

import { RemoteImageObject } from '../../RemoteImageObject/RemoteImageObject';
import { remoteStorageConfiguration } from '../../configuration';
import { LoadStorageFileErrorType } from '../../types';

const REMOTE_IMAGE_LOADING_WAIT_TIMEOUT = 300;

interface IUseRemoteImageParams {
  url: string;
  onLoad?(): void;
  onError?(): void;
}

interface IUseRemoteImageResult {
  src: string;
  isLoading: boolean;
  isError: boolean;
}

export function useRemoteImage(params: IUseRemoteImageParams): IUseRemoteImageResult {
  const { url, onLoad, onError } = params;
  const [src, setSrc] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isWaiting, setIsWaiting] = useState(true);

  const image = useRef<RemoteImageObject | null>(null);

  const loadFile = async (resourceUrl: string): Promise<void> => {
    if (!resourceUrl) {
      setSrc('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    image.current = new RemoteImageObject();
    image.current.src = resourceUrl;

    image.current.onload = (): void => {
      setSrc(image.current?.processedSrc || '');
      setIsError(false);
      setIsLoading(false);
      onLoad?.();
    };

    image.current.onerror = (): void => {
      if (image.current?.errorType === LoadStorageFileErrorType.BadUrl) {
        setSrc(resourceUrl);
      } else {
        setSrc(remoteStorageConfiguration.imageStubSrc);
        setIsError(true);
        onError?.();
      }
      setIsLoading(false);
    };
  };

  // Нужно чтобы не сразу показывать лоадер, а в случае когда изображение закешированно чтобы был минимальный промежуток
  // когда изображение отсутствует. При рендере выглядит почти как мгновенное отображение. Если не закешированно, то появится лоадер через
  // 300мс
  useEffect(() => {
    setIsWaiting(true);
    const timer = setTimeout(() => {
      setIsWaiting(false);
    }, REMOTE_IMAGE_LOADING_WAIT_TIMEOUT);

    return () => {
      clearTimeout(timer);
    };
  }, [src]);

  useEffect(() => {
    loadFile(url);
  }, [url]);

  useEffect(() => {
    return () => {
      image.current?.destroy();
    };
  }, []);

  return { src, isLoading: isLoading && !isWaiting, isError };
}
